import { useEffect, useState } from 'react';
import { SectionColor } from '@/data/types';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const COLOURS = [
  SectionColor.ONE,
  SectionColor.TWO,
  SectionColor.THREE,
  SectionColor.FOUR,
];

interface Props {
  open: boolean;
  /** Existing section being edited; absent when inserting a new one. */
  initialName?: string;
  initialColor?: SectionColor;
  onSave: (name: string, color: SectionColor) => void;
  /** Omitted when inserting — there is nothing to remove yet. */
  onDelete?: () => void;
  onCancel: () => void;
}

/**
 * Title + colour, mirroring the "Titelblock" dialog the technicians used in the
 * old Excel tool (client #30) — same two decisions, same four colours, so the
 * habit carries over.
 */
export function SectionDialog({
  open,
  initialName,
  initialColor,
  onSave,
  onDelete,
  onCancel,
}: Props) {
  const t = useT();
  const [name, setName] = useState(initialName ?? '');
  const [color, setColor] = useState<SectionColor>(
    initialColor ?? SectionColor.ONE
  );

  // Re-seed when the dialog is reopened for a different section.
  useEffect(() => {
    if (open) {
      setName(initialName ?? '');
      setColor(initialColor ?? SectionColor.ONE);
    }
  }, [open, initialName, initialColor]);

  const save = () => {
    const trimmed = name.trim();
    if (trimmed) onSave(trimmed, color);
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {initialName ? t('section.edit') : t('section.add')}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="section-name">{t('section.title')}</Label>
            <Input
              id="section-name"
              data-testid="section-name"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && save()}
              placeholder={t('section.placeholder')}
              autoFocus
            />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('section.colour')}</Label>
            <div className="flex gap-2">
              {COLOURS.map(c => (
                <button
                  key={c}
                  type="button"
                  data-testid={`section-colour-${c}`}
                  aria-label={`${t('section.colour')} ${c}`}
                  aria-pressed={color === c}
                  onClick={() => setColor(c)}
                  className={`section-colour-${c} h-9 flex-1 rounded-md border-2 transition-colors ${
                    color === c ? 'border-primary' : 'border-transparent'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {onDelete ? (
            <Button variant="ghost" onClick={onDelete}>
              {t('section.remove')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button onClick={save} disabled={!name.trim()}>
              {t('common.save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
